import { Interpolation, Theme } from '@emotion/react'
import {
  FC,
  Fragment,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react'
import { animated, useTransition } from 'react-spring'
import { AdOverlayActionCallData } from '../../../contexts/annotations'
import { getSVGRatio, getTimeFromString } from '../../../utils'

export interface Animation {
  show: {
    animatein_type: string
    animatein_duration?: number
  }
  hide?: {
    animateout_type: string
    animateout_duration?: number
  }
}

export interface OverlayPosition {
  bottom?: number
  top?: number
  vcenter?: number
  left?: number
  right?: number
  hcenter?: number
}

export interface OverlayData {
  animations?: Animation
  position: OverlayPosition
  repeat_interval?: number
  delay?: number
  timer?: string
  duration?: number
  opacity?: number
  size: { height?: number; width?: number }
  variables: string[]
  svg_url?: string
  ad_data?: AdOverlayActionCallData
  custom_id: string
}

export interface OverlayProps {
  data: OverlayData
  image?: string
  stageWidth: number
  stageHeight: number
  variables: Map<string, string | number>
  isClickable?: boolean
  children?: ReactNode
}

export const OverlayWrapper: FunctionComponent<OverlayProps> = ({
  data,
  image,
  stageHeight,
  stageWidth,
  variables,
  isClickable,
  children,
}) => {
  const [visible, setVisible] = useState(true)
  const { position, animations, size, opacity } = data

  const animateInDuration = animations?.show?.animatein_duration || 0
  const animateOutDuration = animations?.hide?.animateout_duration || 0

  const ratio = useMemo(() => {
    return (
      (image && getSVGRatio(image)) ||
      (data.size.height &&
        data.size.width &&
        data.size.width / data.size.height) ||
      1
    )
  }, [image, data.size])

  // calculate svg size based on ratio
  const overlayWidth =
    ((size?.width ?? 0) * stageWidth) / 100 ||
    (((size?.height ?? 0) * stageHeight) / 100) * ratio
  const overlayHeight =
    ((size?.height ?? 0) * stageHeight) / 100 ||
    ((size?.width ?? 0) * stageWidth) / 100 / ratio

  const timerStringValue = (data.timer && variables.get(data.timer)) as string
  const timerValue = getTimeFromString(timerStringValue)
  const repeat_interval = (data.repeat_interval || 0) / 1000
  const duration = (data.duration || 0) / 1000 || 0
  const delay = (data.delay || 0) / 1000

  const { top, left } = useMemo(
    () =>
      calculateTopLeftPosition({
        position,
        stageHeight,
        stageWidth,
        overlayWidth,
        overlayHeight,
      }),
    [position, stageHeight, stageWidth, overlayWidth, overlayHeight],
  )

  const componentStyling: Interpolation<Theme> = useMemo(
    () =>
      parseSvgStyling({
        top,
        left,
        overlayWidth,
        overlayHeight,
        size,
        zIndex: isClickable ? 21 : 1,
      }),
    [top, left, overlayWidth, overlayHeight, size, isClickable],
  )

  const animation = useMemo(
    () =>
      getAnimationValues({
        animations,
        animateInDuration,
        animateOutDuration,
        opacity,
        stageHeight,
        stageWidth,
        top,
        left,
      }),
    [
      animations,
      animateInDuration,
      animateOutDuration,
      opacity,
      stageHeight,
      stageWidth,
      top,
      left,
    ],
  )

  const transitions = useTransition(
    visible ? [{ top, left, opacity, key: 'show' }] : [],
    null,
    animation,
  )

  useEffect(() => {
    setVisible(!animations?.hide?.animateout_type)
  }, [animations?.hide?.animateout_type])

  useEffect(() => {
    // Intervals relative to clock
    if (!repeat_interval || data.timer) return

    let intervalShowRef: NodeJS.Timeout | false
    let intervalHideRef: NodeJS.Timeout | false
    let intervalHideDelayRef: NodeJS.Timeout | false
    let delayRef: NodeJS.Timeout | false

    const startTimers = () => {
      setVisible(true)

      intervalHideDelayRef = setTimeout(() => {
        setVisible(false)
        // Hide after duration
        intervalHideRef = setInterval(() => {
          setVisible(false)
        }, repeat_interval * 1000)
      }, duration * 1000)

      intervalShowRef = setInterval(() => {
        // Reshow after repeat interval
        setVisible(true)
      }, repeat_interval * 1000)
    }

    if (!delay) {
      startTimers()
    } else {
      // we keep the overlay hidden for delayed seconds
      setVisible(false)

      delayRef = setTimeout(() => {
        startTimers()
      }, delay)
    }

    return () => {
      delayRef && clearTimeout(delayRef)
      intervalShowRef && clearInterval(intervalShowRef)
      intervalHideRef && clearInterval(intervalHideRef)
      intervalHideDelayRef && clearInterval(intervalHideDelayRef)
    }
  }, [duration, repeat_interval, delay, data.timer])

  useEffect(() => {
    // Intervals relative to timer
    if (!repeat_interval || !data.timer || !duration) return

    if (timerValue < delay) {
      setVisible(false)
      return
    }

    if (timerValue - delay <= duration) {
      setVisible(true)
      return
    }

    if (timerValue % repeat_interval <= duration) {
      if (!visible) setVisible(true)
    } else {
      if (visible) setVisible(false)
    }
  }, [
    delay,
    duration,
    repeat_interval,
    data.timer,
    timerValue,
    variables,
    visible,
  ])

  return (
    <Fragment>
      {transitions.map(({ item, key, props }) => {
        return (
          item && (
            <animated.div
              key={key}
              style={props as any}
              css={componentStyling}
              className="overlay-wrapper"
              id={`overlay-${data.custom_id}`}
              data-testid="overlay-wrapper"
            >
              {children}
            </animated.div>
          )
        )
      })}
    </Fragment>
  )
}

const Overlay: FC<OverlayProps> = (props) => {
  const variablesNames = props.data?.variables || []
  let parsedImage = props.image || ''

  if (variablesNames) {
    variablesNames.map((variableName: string) => {
      parsedImage = parsedImage.replace(
        variableName,
        String(props.variables.get(variableName)),
      )
    })
  }
  return (
    <OverlayWrapper {...props}>
      <span dangerouslySetInnerHTML={{ __html: parsedImage }} />
    </OverlayWrapper>
  )
}

export default Overlay

export const parseSvgStyling = ({
  top,
  left,
  overlayWidth,
  overlayHeight,
  size: { height = 0, width = 0 } = { height: 0, width: 0 },
  zIndex,
}: {
  overlayWidth: number
  overlayHeight: number
  left: number
  top: number
  size?: {
    height?: number
    width?: number
  }
  zIndex: number
}): Interpolation<Theme> => {
  return {
    height: overlayHeight
      ? overlayHeight.toFixed(2) + 'px'
      : height
      ? height + '%'
      : 'auto',
    maxHeight: height ? height + '%' : undefined,
    width: overlayWidth
      ? overlayWidth.toFixed(2) + 'px'
      : width
      ? width + '%'
      : 'auto',
    maxWidth: width ? width + '%' : undefined,
    zIndex,
    opacity: 1,
    overflow: 'hidden',
    position: 'absolute' as const,
    svg: {
      height: '100%!important', // needed because svgs might have inline styling
      width: '100%!important',
    },
    img: {
      height: '100%',
    },
    left: left || '0',
    top: top || '0',
  }
}

const getAnimationValues = ({
  animations,
  left,
  top,
  opacity,
  animateInDuration,
  animateOutDuration,
  stageHeight,
  stageWidth,
}: {
  animations?: Animation
  left: number
  top: number
  opacity?: number
  animateInDuration: number
  animateOutDuration: number
  stageHeight: number
  stageWidth: number
}) => {
  const enterState =
    ({
      // tslint:disable-next-line: no-shadowed-variable
      left = 0,
      // tslint:disable-next-line: no-shadowed-variable
      top = 0,
    }: {
      left: number
      top: number
      key: string
    }) =>
    async (
      next: ({
        left,
        top,
        opacity,
      }: {
        left: number
        top: number
        opacity: number
      }) => Promise<void>,
    ) => {
      try {
        await next({ left, top, opacity: opacity || 1 })
      } catch (error) {
        console.error(error)
      }
    }

  const transition: any = {
    from: { left: 0, top: 0, opacity: 0 },
    enter: enterState,
    update: enterState,
    leave: { left, top, opacity: opacity || 1 },
    config: { duration: animateInDuration },
  }

  if (animations?.show) {
    switch (animations?.show?.animatein_type) {
      case 'fade_in':
        transition.from = { left, top, opacity: 0 }
        break
      case 'slide_from_left':
        transition.from = { top, left: -stageWidth, opacity: opacity || 1 }
        break
      case 'slide_from_top':
        transition.from = { left, top: -stageHeight, opacity: opacity || 1 }
        break
      case 'slide_from_bottom':
        transition.from = { left, top: stageHeight, opacity: opacity || 1 }
        break
      case 'slide_from_right':
        transition.from = { top, left: stageWidth, opacity: opacity || 1 }
        break
      case 'none':
      default:
        transition.from = { left, top, opacity: opacity || 1 }
    }
  }

  if (animations?.hide) {
    switch (animations?.hide?.animateout_type) {
      case 'fade_out':
        transition.leave = { left, top, opacity: 0 }
        break
      case 'slide_to_left':
        transition.leave = { left: -stageWidth, top, opacity: opacity || 1 }
        break
      case 'slide_to_top':
        transition.leave = { left, top: -stageHeight, opacity: opacity || 1 }
        break
      case 'slide_to_bottom':
        transition.leave = { left, top: stageHeight, opacity: opacity || 1 }
        break
      case 'slide_to_right':
        transition.leave = { left: stageWidth, top, opacity: opacity || 1 }
        break
      case 'none':
      default:
        transition.leave = { left, top, opacity: 0 }
        transition.config.duration = 0
    }

    transition.config.duration =
      (animations?.hide?.animateout_type !== 'none' && animateOutDuration) || 0
  }

  return transition
}

export const calculateTopLeftPosition = ({
  position,
  stageHeight,
  stageWidth,
  overlayWidth,
  overlayHeight,
}: {
  position?: OverlayPosition
  stageHeight: number
  stageWidth: number
  overlayWidth: number
  overlayHeight: number
}): {
  top: number
  left: number
} => {
  let top = 0
  let left = 0

  if (!position || !stageHeight || !stageWidth) {
    return { top, left }
  }

  if (position.top || position.top === 0) {
    top = (position.top * stageHeight) / 100
  }
  if (position.bottom) {
    top = stageHeight - (position.bottom * stageHeight) / 100 - overlayHeight
  }
  if (position.vcenter || position.vcenter === 0) {
    top =
      stageHeight * 0.5 -
      overlayHeight * 0.5 +
      (position.vcenter * stageHeight) / 100
  }

  if (position.left || position.left === 0) {
    left = (position.left * stageWidth) / 100
  }
  if (position.right || position.right === 0) {
    left = stageWidth - (position.right * stageWidth) / 100 - overlayWidth
  }
  if (position.hcenter || position.hcenter === 0) {
    left =
      stageWidth * 0.5 -
      overlayWidth * 0.5 +
      (position.hcenter * stageWidth) / 100
  }

  return { top, left }
}
